import type { AdminProductFilter, AdminProductSort } from "@listou/shared/schemas/admin";
import type { AdminSite } from "@listou/shared/types/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { ProductControls, ProductsTable } from "../components/admin/products";
import {
  Box,
  euros,
  Favicon,
  Grid,
  n,
  Pending,
  percent,
  Row,
  Table,
  useAdminDays,
} from "../components/admin/retro";
import { adminProductsQuery } from "../lib/queries";

export const Route = createFileRoute("/_authed/admin/sites/$host")({
  component: SitePage,
});

const TIER_LABELS: Record<string, string> = {
  bee: "scrapingbee standard",
  "bee-premium": "scrapingbee résidentiel",
  "bee-js": "scrapingbee avec js",
  "amazon-api": "api amazon",
  serper: "serper",
  shopify: "shopify (gratuit)",
  direct: "direct (gratuit)",
};

function SitePage() {
  const { host } = Route.useParams();
  const days = useAdminDays();
  const [sort, setSort] = useState<AdminProductSort>("wishes");
  const [filter, setFilter] = useState<AdminProductFilter>("all");
  const page = useQuery(adminProductsQuery({ host, sort, filter }));

  return (
    <Grid>
      <div className="md:col-span-2 xl:col-span-4 flex items-center gap-3">
        <Link to="/admin/sites" search={{ j: days }} className="retro-btn">
          ← sites
        </Link>
        <span className="inline-flex items-center gap-2 text-base" style={{ color: "var(--r-hi)" }}>
          <Favicon host={host} />
          {host}
        </span>
        <a
          href={`https://${host}`}
          target="_blank"
          rel="noreferrer"
          className="hover:underline"
          style={{ color: "var(--r-dim)" }}
        >
          ouvrir le site ↗
        </a>
      </div>

      {page.data?.site ? <SiteSummary site={page.data.site} /> : <Pending query={page} />}

      <Box
        title="produits"
        className="md:col-span-2 xl:col-span-4"
        right={
          <ProductControls sort={sort} filter={filter} onSort={setSort} onFilter={setFilter} />
        }
      >
        {page.data ? (
          <div className={page.isPlaceholderData ? "opacity-60" : undefined}>
            <ProductsTable products={page.data.products} showShop={false} />
          </div>
        ) : (
          <Pending query={page} />
        )}
      </Box>
    </Grid>
  );
}

function SiteSummary({ site }: { site: AdminSite }) {
  const credits = site.scraping.reduce((total, tier) => total + tier.credits, 0);
  return (
    <>
      <Box title="catalogue">
        <Row label="produits">{n(site.products)}</Row>
        <Row label="  sans prix" tone={site.missingPrice ? "warn" : undefined}>
          {n(site.missingPrice)} ({percent(site.missingPrice, site.products)})
        </Row>
        <Row label="  sans photo" tone={site.missingImage ? "warn" : undefined}>
          {n(site.missingImage)} ({percent(site.missingImage, site.products)})
        </Row>
      </Box>
      <Box title="ce qu'il apporte">
        <Row label="envies">{n(site.wishes)}</Row>
        <Row label="listes">{n(site.lists)}</Row>
        <Row label="cadeaux réservés" tone={site.reservedQuantity ? "ok" : undefined}>
          {n(site.reservedQuantity)}
        </Row>
        <Row label="valeur réservée" tone={site.reservedValueCents ? "ok" : undefined}>
          {euros(site.reservedValueCents)}
        </Row>
        <Row label="clics 90 j / 12 mois">
          {n(site.clicks90)} / {n(site.clicks365)}
        </Row>
      </Box>
      <Box title="lecture" right="30 j" className="md:col-span-2">
        <Row label="niveau de départ">
          {site.memory.tier
            ? (TIER_LABELS[site.memory.tier] ?? site.memory.tier)
            : "pas encore appris"}
        </Row>
        <Row label="état" tone={site.memory.blocked ? "bad" : site.memory.misses ? "warn" : "ok"}>
          {site.memory.blocked
            ? "bloqué : seuls les niveaux gratuits sont tentés"
            : site.memory.misses
              ? `${site.memory.misses} échec(s) de suite`
              : "ok"}
        </Row>
        <Row label="crédits payants">{n(credits)}</Row>
        <div className="mt-1">
          <Table
            head={["niveau", "appels", "échecs", "crédits"]}
            rows={site.scraping.map((tier) => [
              TIER_LABELS[tier.tier] ?? tier.tier,
              tier.calls,
              {
                value: tier.failures ? n(tier.failures) : "-",
                tone: tier.failures ? "bad" : "dim",
              },
              tier.credits,
            ])}
            empty="aucune lecture payante"
          />
        </div>
      </Box>
    </>
  );
}
